import { type ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import {
  selectIsAuthenticated,
  useAuthStore,
} from '@/features/auth/store/auth.store'
import {
  AUTH_STORAGE_KEYS,
} from '@/features/auth/constants/auth.constants'
import { AUTH_ROUTES } from '@/shared/constants/routes.constants'
import { useIdleAwayDetection } from '@/features/presence/hooks/useIdleAwayDetection'
import { useOwnUserChannel } from '@/features/messenger/hooks/useOwnUserChannel'
import { useContactRegistration } from '@/features/messenger/hooks/useContactRegistration'
import { usePushRegistration } from '@/features/push/usePushRegistration'
import { GlobalCallLayer } from '@/features/messenger/GlobalCallLayer'

interface ProtectedRouteProps {
  children: ReactNode
  /**
   * Let signed-out visitors through as guests. Set on `/meeting/:id` so a
   * shared link works without a query flag: guest-ness is decided by whether
   * you have a session, not by a parameter in the URL that can be dropped when
   * the link is pasted around.
   */
  allowGuest?: boolean
}

/**
 * Gate for authenticated-only pages. If no valid session exists the current
 * path is remembered and the user is redirected to /login.
 */
export default function ProtectedRoute({ children, allowGuest = false }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const status = useAuthStore((s) => s.status)
  const location = useLocation()

  // Idle auto-away runs only while authed. Hook is internally guarded against
  // missing user id, so calling it unconditionally is safe.
  useIdleAwayDetection()

  // Resolve the per-org `contactPresenceId` on EVERY authed route, not just the
  // dashboard/chat surfaces. The owner channel below and every chat subscription
  // are gated on it, so a user who lands directly on a non-dashboard route (a
  // shared /meeting link, a deep link, a hard reload there) would otherwise never
  // resolve an identity: permanently offline to others, and no live messages. The
  // hook self-dedupes once the id is known (persisted session or another mount),
  // so this costs nothing. The T&C gate stays a chat-surface concern.
  useContactRegistration()

  // Own `user:<presenceId>` topic — a SINGLE owner channel held open on every
  // authed page (broadest coverage — meeting rooms too). It both (a) owner-
  // tracks my presence and mirrors my own status back into the store (cross-tab
  // changes, in-meeting auto-busy, `presence:offline_confirmed`) AND (b) listens
  // for `conversation:added` / `conversation:activity` so a brand-new incoming
  // chat shows live. These MUST share one channel — Phoenix closes a duplicate
  // join on the same (socket, topic), so two channels here would kill each
  // other. `usePresenceSubscription` (PresencePage) now only WATCHES OTHER
  // users, never my own id, so the two never collide.
  //
  // PRESENCE keys on the per-org `contactPresenceId` (not the auth user id) —
  // restored from the persisted session on reload, else resolved by the hook
  // above; null only until first consent (terms gate), when the channel simply
  // doesn't start. The socket still authenticates as the auth user.
  useOwnUserChannel()

  // Register this browser for FCM push (closed-app chat alerts) once authed.
  usePushRegistration()

  useEffect(() => {
    if (!isAuthenticated && status !== 'hydrating') {
      localStorage.setItem(
        AUTH_STORAGE_KEYS.REQUESTED_PATH,
        location.pathname + location.search,
      )
    }
  }, [isAuthenticated, status, location.pathname, location.search])

  // Guests are admitted via navigation state (guestMode — set by the
  // /invite/:token flow) or by `allowGuest` on a shareable meeting route.
  // Either way, let them through without an auth session; the actual guest
  // join happens on the "Join" click inside VcApp.
  const isGuestAdmission = !!(location.state as any)?.guestMode || allowGuest

  if (status === 'hydrating') return null
  if (!isAuthenticated && !isGuestAdmission) {
    return <Navigate to={AUTH_ROUTES.LOGIN} replace />
  }

  return (
    <>
      {children}
      <GlobalCallLayer />
    </>
  )
}
